import { FC, useEffect, useMemo, useState } from 'react';

import { Button, Stack } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useGetMethods } from 'widgets/methods';

import { TGenerations } from 'shared/services/types/generations.types';
import { FormWrapper } from 'shared/ui/form-wrapper';
import { ModalTemplate } from 'shared/ui/modal-template';
import { addMessageToast, hasFormValuesChanged } from 'shared/utils';

import { useGenerationsActions } from '../api';

import { GenerationForm, GenerationInfo } from './slices';

interface IProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}

export const GenerationModal: FC<IProps> = ({ isOpen, setIsOpen, onClose }) => {
  const methods = useForm({ mode: 'onBlur' });
  const [isStarted, setIsStarted] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const { reset } = methods;
  const isConfirmationNeeded = hasFormValuesChanged(methods.watch(), {});
  const { startGeneration, checkGeneration, stopGeneration } = useGenerationsActions();

  const { methods: methodsData } = useGetMethods();
  // const { data: methodGeneration } = checkGeneration;

  const methodGeneration = {} as TGenerations.checkIsGenerationResponse;

  useEffect(() => {
    setIsStarted(!!methodGeneration?.isActive);
  }, [methodGeneration]);

  const methodsOptions = useMemo(() => {
    return methodsData?.map(item => ({ label: item.name, value: item.id })) || [];
  }, [methodsData]);

  const requiredFildes = Object.keys(methods.control._fields).filter(key => {
    const isRequired = methods.control._fields[key]?._f.required;
    return isRequired;
  });

  const emptyRequiredFildes = requiredFildes.filter(rf => {
    const isEmpty = !methods.watch(rf);
    return isEmpty;
  });

  useEffect(() => {
    if (requiredFildes.length) setIsDisabled(!!emptyRequiredFildes.length);
  }, [emptyRequiredFildes]);

  const onSubmit: SubmitHandler<TGenerations.startGenerations> = async data => {
    try {
      setIsStarted(prev => !prev);
      if (!isStarted) {
        await startGeneration.mutateAsync(data);
        addMessageToast('Generator started', 'success');
      } else {
        await stopGeneration.mutateAsync();
        addMessageToast('Generator stoped', 'error');
      }
      setIsOpen(false);
      reset();
    } catch (error) {
      addMessageToast(error);
    }
  };

  return (
    <ModalTemplate
      isConfirmationNeeded={isConfirmationNeeded}
      close={onClose}
      isOpen={isOpen}
      titleText={isStarted ? 'The generator with these parameters is running' : 'Generation'}
      reset={reset}
      sx={{
        '.modal-title': {
          padding: '0 24px'
        }
      }}>
      <FormWrapper onSubmit={onSubmit} methods={methods}>
        <Stack gap={isStarted ? '15px' : '30px'} flexWrap='nowrap' alignItems='stretch'>
          {!isStarted ? (
            <GenerationInfo methodGeneration={methodGeneration} />
          ) : (
            <GenerationForm methodsOptions={methodsOptions} />
          )}
        </Stack>
        <Stack direction='row' gap='16px' p={'30px 0 0'} justifyContent='space-between'>
          {!isStarted ? (
            <>
              <Button
                type='submit'
                fullWidth
                variant='contained'
                color='error'
                sx={{ borderRadius: 50, textTransform: 'none', height: 40, width: 170 }}>
                Stop
              </Button>
              <Button
                type='button'
                fullWidth
                variant='contained'
                onClick={() => setIsOpen(false)}
                sx={{ borderRadius: 50, textTransform: 'none', height: 40, width: 179 }}>
                Close
              </Button>
            </>
          ) : (
            <Button
              type='submit'
              fullWidth
              variant='contained'
              disabled={isDisabled}
              sx={{ borderRadius: 50, textTransform: 'none', height: 40, width: 1 }}>
              Start
            </Button>
          )}
        </Stack>
      </FormWrapper>
    </ModalTemplate>
  );
};
